import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const MusicCartContext = createContext(null);

// Bundle pricing configuration
const BUNDLE_PRICING = {
  3: { price: 2.49, savings: 0.48 },  // 3 tracks for $2.49 (save $0.48)
  5: { price: 3.99, savings: 0.96 },  // 5 tracks for $3.99 (save $0.96)
  10: { price: 6.99, savings: 2.91 }, // 10 tracks for $6.99 (save $2.91)
};

export const useMusicCart = () => {
  const context = useContext(MusicCartContext);
  if (!context) {
    throw new Error('useMusicCart must be used within a MusicCartProvider');
  }
  return context;
};

export const MusicCartProvider = ({ children }) => {
  const [tracks, setTracks] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addTrack = useCallback((track) => {
    setTracks(prev => {
      // Don't add duplicates
      if (prev.find(t => t.id === track.id)) {
        return prev;
      }
      return [...prev, track];
    });
    setIsOpen(true);
  }, []);

  const removeTrack = useCallback((trackId) => {
    setTracks(prev => prev.filter(t => t.id !== trackId));
  }, []);

  const clearCart = useCallback(() => {
    setTracks([]);
  }, []);

  const toggleCart = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeCart = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openCart = useCallback(() => {
    setIsOpen(true);
  }, []);

  const isInCart = useCallback((trackId) => {
    return tracks.some(t => t.id === trackId);
  }, [tracks]);

  // Calculate pricing with bundle discounts
  const pricing = useMemo(() => {
    const regularTotal = tracks.reduce((sum, track) => sum + (track.price || 0.99), 0);
    const trackCount = tracks.length;
    
    // Check for applicable bundle
    let bundleApplied = null;
    let finalPrice = regularTotal;
    let savings = 0;
    
    // Find the best applicable bundle
    const bundleTiers = Object.keys(BUNDLE_PRICING).map(Number).sort((a, b) => b - a);
    for (const tier of bundleTiers) {
      if (trackCount >= tier) {
        bundleApplied = tier;
        finalPrice = BUNDLE_PRICING[tier].price;
        savings = regularTotal - finalPrice;
        break;
      }
    }

    // Calculate tracks needed for next bundle
    let tracksToNextBundle = null;
    let nextBundlePrice = null;
    const nextTier = bundleTiers.reverse().find(tier => tier > trackCount);
    if (nextTier) {
      tracksToNextBundle = nextTier - trackCount;
      nextBundlePrice = BUNDLE_PRICING[nextTier].price;
    }

    return {
      regularTotal,
      finalPrice,
      savings,
      bundleApplied,
      tracksToNextBundle,
      nextBundlePrice
    };
  }, [tracks]);

  const trackCount = tracks.length;

  // Split memoization: cart state vs cart actions
  const cartState = React.useMemo(() => ({
    tracks,
    isOpen,
    total: pricing.finalPrice,
    regularTotal: pricing.regularTotal,
    savings: pricing.savings,
    bundleApplied: pricing.bundleApplied,
    tracksToNextBundle: pricing.tracksToNextBundle,
    nextBundlePrice: pricing.nextBundlePrice,
    trackCount,
  }), [tracks, isOpen, pricing, trackCount]);

  const cartActions = React.useMemo(() => ({
    addTrack,
    removeTrack,
    clearCart,
    toggleCart,
    closeCart,
    openCart,
    isInCart
  }), [addTrack, removeTrack, clearCart, toggleCart, closeCart, openCart, isInCart]);

  const value = React.useMemo(() => ({
    ...cartState,
    ...cartActions
  }), [cartState, cartActions]);

  return (
    <MusicCartContext.Provider value={value}>
      {children}
    </MusicCartContext.Provider>
  );
};
